import React from "react";
import { titleAndAria } from "protocol-setup/constants/errorMessages";
import { useFormState } from "react-hook-form";
import {
  FasPlus,
  FasUndoAlt, 
  FasSave,
  FasTrashAlt
} from 'assets/icons';
import SubmitRequestBtn from "./SubmitRequestBtn";

const getValidationState = (errors) => {
  const errorCount = Object.keys(errors || {}).length;
  const errorFields = Object.keys(errors || {});
  
  return {
    errorCount,
    errorFields,
    hasErrors: errorCount > 0,
    errorMessage: errorCount > 0 
      ? `Form has ${errorCount} error${errorCount !== 1 ? 's' : ''}`
      : 'Form is valid'
  };
};

const FormButtonGroup = ({
  control,
  formTitle,
  reset,
  requestNumber,
  formPartType,
  hideSubmitRequest = false,
  isSubmitFormEnabled = false,
  setIsSubmitFormEnabled,
  onSuccess,
  addDisableClass = false,
  saveLabel = "Save"
}) => {
  const { isValid, isDirty, errors } = useFormState({ control });
  const validationState = getValidationState(errors);

  return (
    <div className="form-layout-actions align-center">
      <button
        title={titleAndAria.clearChanges}
        type="button"
        data-testid="reset-button"
        className={` fn-btn mr-1 ${!isDirty ? "disabled color-grey-disabled" : "color-dark-blue"}`}
        disabled={!isDirty}
        onClick={() => reset()}
      >
        <FasUndoAlt />
        Clear Changes
      </button>
      {!hideSubmitRequest && (
        <SubmitRequestBtn
          requestNumber={requestNumber}
          formPartType={formPartType}
          isSubmitFormEnabled={isSubmitFormEnabled}
          setIsSubmitFormEnabled={setIsSubmitFormEnabled}
          onSuccess={onSuccess}
        />
      )}
      <button
        data-testid="submit-button"
        title={validationState.hasErrors ? `Save (${validationState.errorMessage})` : "Save"}
        type="submit"
        className="btn turquoise mr-1"
        aria-label={validationState.hasErrors ? `Save (${validationState.errorMessage})` : "Save"}
        {...(addDisableClass ? { className: `btn ${isDirty && isValid ? "turquoise" : "disabled"} mr-1` } : { disabled: !isValid || !isDirty })}
      >
        <FasSave className="ml-n1"/>
        {saveLabel}
      </button>
    </div>
  );
};

const FormButtonGroupHeader = ({ control, reset, formTitle }) => {
  const { isValid, isDirty } = useFormState({ control });

  return (
    <div className="d-flex justify-content-end align-items-center mr-2 mb-2">
      <button
        title={titleAndAria.saveRequest}
        className="fn-btn"
        type="submit"
        disabled={!isValid || !isDirty}
        data-testid="submit-button-icon"
      >
        <FasSave />
      </button>
      <button
        title={titleAndAria.clearChanges}
        className={` fn-btn ${!isDirty ? "disabled color-grey-disabled" : "color-dark-blue"}`}
        disabled={!isDirty}
        type="button"
        onClick={() => reset()}
        data-testid="reset-button-icon"
      >
        <FasUndoAlt />
      </button>
    </div>
  );
};

function AddNewRowButton({ onClick, text, title = titleAndAria.addNewRow, readOnly, refForward }) {
  if (readOnly) {
    return null;
  }
  return (
    <button
      onClick={onClick}
      className="btn turquoise my-1 "
      type="button"
      title={title}
      aria-label={title}
      data-testid="add-new-row-button"
      ref={refForward}
    >
      <FasPlus className="ml-n1"/>
      {text}
    </button>
  );
}

const ButtonActions = ({
  onUndo,
  onDelete,
  index,
  hideUndo = true,
  undoTitle = titleAndAria.clearChanges,
  title = titleAndAria.delete,
  disabled = false,
  forwardRef,
  watch,
  fieldName,
  control
}) => {
  const { dirtyFields, touchedFields } = useFormState({ control });
  
  // Navigate to the specific field in dirtyFields and touchedFields
  const getNestedField = (obj, path) => {
    if (!obj || !path) return undefined;
    return path.split('.').reduce((acc, part) => {
      const arrayMatch = part.match(/(\w+)\[(\d+)\]/);
      if (arrayMatch) {
        const [, key, idx] = arrayMatch;
        return acc?.[key]?.[parseInt(idx, 10)];
      }
      return acc?.[part];
    }, obj);
  };

  const fieldDirty = getNestedField(dirtyFields, fieldName);
  const fieldTouched = getNestedField(touchedFields, fieldName);
  const fieldValue = fieldName ? watch(fieldName) : undefined;
  
  const hasChanges = fieldDirty && fieldTouched && fieldValue !== undefined;
  const disabledUndo = !hasChanges;

  return (
    <div
      className={`d-flex pl-2 py-2 ${index === 0 ? "mt-4" : ""}`}
      style={{backgroundColor: 'hsl(0deg 0% 100% / 50%)', borderRadius: '0 4px 4px 0', height: '3rem'}} // last
    >
      <button
        onClick={onUndo}
        className="fn-btn mx-2"
        type="button"
        title={undoTitle}
        disabled={disabledUndo}
        data-testid="undo-button-icon"
      >
        <FasUndoAlt />
        <span className="sr-only">{titleAndAria.clearChanges}</span>
      </button>

      <button
        onClick={onDelete}
        className="fn-btn delete"
        type="button"
        title={title}
        disabled={disabled ? "disabled" : ""}
        data-testid="delete-button-icon"
        ref={forwardRef}
      >
        <FasTrashAlt />
        <span className="sr-only">{titleAndAria.delete}</span>
      </button>

    </div>
  );
};

export {
  FormButtonGroup,
  FormButtonGroupHeader,
  AddNewRowButton,
  ButtonActions
};